import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import { refreshPublicSettings } from "@/lib/settings";
import AdminLayout from "@/components/AdminLayout";
import PushNotificationsToggle from "@/components/PushNotificationsToggle";
import { Loader2, Save, Building2, Banknote, Receipt, Globe, FileText, BellRing, Clock, CalendarX, Plus, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

const Section = ({ icon: Icon, title, sub, children }) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/[0.07] overflow-hidden">
    <div className="p-5 sm:p-6 border-b border-white/5 flex items-start gap-3">
      <div className="size-9 rounded-md bg-spark-yellow/15 text-spark-yellow flex items-center justify-center shrink-0">
        <Icon className="size-4" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-spark-yellow font-bold">Settings</p>
        <h2 className="font-display uppercase tracking-tight text-xl font-bold text-white mt-0.5">{title}</h2>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
    <div className="p-5 sm:p-6 grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">{label}</label>
    <div className="mt-2">{children}</div>
    {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

const tx = "w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-spark-teal";

const AdminSettings = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((res) => setForm(res.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          setAdminToken(null); navigate("/admin/login");
        } else toast.error("Failed to load settings");
      });
  }, [navigate]);

  if (!form) {
    return <AdminLayout title="Loading…"><div className="text-center py-20 text-slate-400"><Loader2 className="size-5 animate-spin inline" /></div></AdminLayout>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.updated_at;
      const res = await api.patch("/admin/settings", payload);
      setForm(res.data);
      // Bump the public settings cache so footer/contact reflect changes immediately
      try { await refreshPublicSettings(); } catch (e) { /* ignore */ }
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      eyebrow="Workshop config"
      title="Business Settings"
      actions={
        <button onClick={submit} disabled={saving} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-spark-yellow-dark disabled:opacity-50" data-testid="save-settings-top">
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} Save changes
        </button>
      }
    >
      <form onSubmit={submit} className="space-y-5 max-w-5xl">
        <Section icon={Building2} title="Business identity" sub="Used on quotes, invoices, receipts, and the public footer.">
          <Field label="Trading name"><input className={tx} value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="settings-business-name" /></Field>
          <Field label="Legal name (if different)"><input className={tx} value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} /></Field>
          <Field label="Address" full><input className={tx} value={form.address_line || ""} onChange={(e) => set("address_line", e.target.value)} /></Field>
          <Field label="City"><input className={tx} value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label="Region"><input className={tx} value={form.region || ""} onChange={(e) => set("region", e.target.value)} /></Field>
          <Field label="Postcode"><input className={tx} value={form.postcode || ""} onChange={(e) => set("postcode", e.target.value)} /></Field>
          <Field label="Country"><input className={tx} value={form.country || ""} onChange={(e) => set("country", e.target.value)} /></Field>
          <Field label="Phone"><input className={tx} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Public email"><input className={tx} type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Website" full><input className={tx} value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
        </Section>

        <Section icon={Banknote} title="Bank transfer details" sub="Printed on invoices so customers can pay you direct (no POS fee).">
          <Field label="Account name"><input className={tx} value={form.bank_account_name || ""} onChange={(e) => set("bank_account_name", e.target.value)} placeholder="Spark Scooter Repairs" data-testid="settings-bank-name" /></Field>
          <Field label="Bank"><input className={tx} value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} placeholder="BNZ / ASB / ANZ…" /></Field>
          <Field label="Account number" full hint="NZ format: 02-1234-5678901-00">
            <input className={tx} value={form.bank_account_number || ""} onChange={(e) => set("bank_account_number", e.target.value)} placeholder="00-0000-0000000-00" data-testid="settings-bank-account" />
          </Field>
          <Field label="Payment reference">
            <input className={tx} value={form.payment_reference_format || ""} onChange={(e) => set("payment_reference_format", e.target.value)} />
          </Field>
          <Field label="Payment terms (days)">
            <input className={tx} type="number" min={0} value={form.payment_terms_days ?? 7} onChange={(e) => set("payment_terms_days", Number(e.target.value || 0))} />
          </Field>
        </Section>

        <Section icon={Receipt} title="Tax / GST" sub="When enabled, invoices &amp; receipts include GST breakdown and your IRD number.">
          <div className="sm:col-span-2 flex items-center gap-3 p-3 rounded-md bg-white/[0.03] border border-white/5">
            <input type="checkbox" id="gst-toggle" checked={!!form.gst_registered} onChange={(e) => set("gst_registered", e.target.checked)} className="size-4 accent-spark-yellow" data-testid="settings-gst-toggle" />
            <label htmlFor="gst-toggle" className="text-sm text-white cursor-pointer flex-1">
              <span className="font-display uppercase tracking-wider font-bold text-spark-yellow">GST registered</span>
              <span className="block text-xs text-slate-400 mt-0.5">Tick this once your turnover is over $60k/yr or you've voluntarily registered.</span>
            </label>
          </div>
          <Field label="GST / IRD number">
            <input className={tx} value={form.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} placeholder="123-456-789" disabled={!form.gst_registered} data-testid="settings-gst-number" />
          </Field>
          <Field label="GST rate" hint="NZ default = 15%">
            <input className={tx} type="number" step="0.01" value={form.gst_rate ?? 0.15} onChange={(e) => set("gst_rate", Number(e.target.value || 0))} disabled={!form.gst_registered} />
          </Field>
          <div className="sm:col-span-2 flex items-center gap-3 p-3 rounded-md bg-white/[0.03] border border-white/5">
            <input type="checkbox" id="gst-incl" checked={!!form.gst_inclusive_pricing} onChange={(e) => set("gst_inclusive_pricing", e.target.checked)} className="size-4 accent-spark-yellow" disabled={!form.gst_registered} />
            <label htmlFor="gst-incl" className="text-sm text-white cursor-pointer flex-1">
              <span className="font-display uppercase tracking-wider font-bold">Prices are GST-inclusive</span>
              <span className="block text-xs text-slate-400 mt-0.5">If on, the price you charge already contains GST. Invoices will back-out the GST portion.</span>
            </label>
          </div>
        </Section>

        <Section icon={Globe} title="Social / online presence" sub="Appears in email footers and on the public Contact page.">
          <Field label="Facebook URL"><input className={tx} value={form.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://facebook.com/…" /></Field>
          <Field label="Instagram URL"><input className={tx} value={form.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/…" /></Field>
          <Field label="TikTok URL"><input className={tx} value={form.tiktok_url || ""} onChange={(e) => set("tiktok_url", e.target.value)} placeholder="https://tiktok.com/@…" /></Field>
          <Field label="Google Maps URL"><input className={tx} value={form.google_maps_url || ""} onChange={(e) => set("google_maps_url", e.target.value)} /></Field>
        </Section>

        <Section icon={FileText} title="Quote / Invoice settings" sub="Defaults applied to every formal document you send.">
          <Field label="Quote validity (days)">
            <input className={tx} type="number" min={1} value={form.quote_validity_days ?? 14} onChange={(e) => set("quote_validity_days", Number(e.target.value || 0))} />
          </Field>
          <Field label="Invoice footer note" full hint="Optional thank-you, warranty terms, etc. Appears at the bottom of every invoice.">
            <textarea rows={3} className={tx + " resize-none"} value={form.invoice_footer_note || ""} onChange={(e) => set("invoice_footer_note", e.target.value)} />
          </Field>
        </Section>

        <BusinessHoursSection hours={form.business_hours} onChange={(v) => set("business_hours", v)} />
        <HolidayClosuresSection closures={form.holiday_closures || []} onChange={(v) => set("holiday_closures", v)} />

        <Section icon={Truck} title="Pickup & Drop-off service" sub="Flip this off when your vehicle's out of action — hides the booking option and shows a notice on the home page.">
          <div className="sm:col-span-2 flex items-center gap-3 p-3 rounded-md bg-white/[0.03] border border-white/5">
            <input
              type="checkbox"
              id="logistics-toggle"
              checked={!!form.logistics_available}
              onChange={(e) => set("logistics_available", e.target.checked)}
              className="size-4 accent-spark-yellow"
              data-testid="settings-logistics-available-toggle"
            />
            <label htmlFor="logistics-toggle" className="text-sm text-white cursor-pointer flex-1">
              <span className="font-display uppercase tracking-wider font-bold text-spark-yellow">Pickup &amp; drop-off currently available</span>
              <span className="block text-xs text-slate-400 mt-0.5">
                {form.logistics_available
                  ? "Customers can tick the +$40 pickup / return options on the booking form."
                  : "Service is paused — the booking option is hidden and a yellow banner appears on the Home &amp; Booking pages."}
              </span>
            </label>
          </div>
          <Field label="Banner message (optional)" full hint="Shown on the public banner when the service is paused. Leave blank to use the friendly default.">
            <textarea
              rows={3}
              className={tx + " resize-none"}
              value={form.logistics_unavailable_message || ""}
              onChange={(e) => set("logistics_unavailable_message", e.target.value)}
              placeholder="e.g. Our van is in the shop until Friday — all in-workshop services running as normal."
              disabled={!!form.logistics_available}
              data-testid="settings-logistics-message"
            />
          </Field>
        </Section>

        <Section icon={BellRing} title="Push notifications" sub="Get pinged on your phone/tablet the moment a job comes in. Setting is per-device — enable on each one you use.">
          <PushNotificationsToggle />
        </Section>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-spark-yellow text-spark-ink font-display uppercase tracking-wider font-bold hover:bg-spark-yellow-dark disabled:opacity-50" data-testid="save-settings-bottom">
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save changes
          </button>
        </div>
      </form>
    </AdminLayout>
  );
};

const DAYS = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const DEFAULT_HOURS = { open: true, open_time: "09:00", close_time: "17:00" };

const BusinessHoursSection = ({ hours, onChange }) => {
  const value = hours || {};
  const patch = (dayKey, day) => onChange({ ...value, [dayKey]: day });
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/[0.07] overflow-hidden" data-testid="hours-section">
      <div className="p-5 sm:p-6 border-b border-white/5 flex items-start gap-3">
        <div className="size-9 rounded-md bg-spark-yellow/15 text-spark-yellow flex items-center justify-center shrink-0">
          <Clock className="size-4" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-spark-yellow font-bold">Settings</p>
          <h2 className="font-display uppercase tracking-tight text-xl font-bold text-white mt-0.5">Operating hours</h2>
          <p className="text-xs text-slate-400 mt-1">Customers can only pick booking times inside these hours. Change them any time — the booking page updates instantly.</p>
        </div>
      </div>
      <ul className="divide-y divide-white/5">
        {DAYS.map(({ key, label }) => {
          const d = value[key] || DEFAULT_HOURS;
          return (
            <li key={key} className="px-5 sm:px-6 py-3 flex items-center gap-3 flex-wrap" data-testid={`hours-row-${key}`}>
              <span className="w-12 font-display uppercase tracking-tight font-bold text-white text-sm">{label}</span>
              <label className="inline-flex items-center gap-2 text-xs text-slate-300 min-w-[110px]">
                <input
                  type="checkbox"
                  checked={!!d.open}
                  onChange={(e) => patch(key, { ...d, open: e.target.checked })}
                  className="size-4 accent-spark-teal"
                  data-testid={`hours-open-${key}`}
                />
                {d.open ? "Open" : "Closed"}
              </label>
              <div className={`flex items-center gap-2 flex-wrap ${d.open ? "" : "opacity-40"}`}>
                <input
                  type="time"
                  value={d.open_time || "09:00"}
                  disabled={!d.open}
                  step={900}
                  onChange={(e) => patch(key, { ...d, open_time: e.target.value })}
                  className="bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-spark-teal disabled:opacity-60"
                  data-testid={`hours-open-time-${key}`}
                />
                <span className="text-slate-500 text-xs">to</span>
                <input
                  type="time"
                  value={d.close_time || "17:00"}
                  disabled={!d.open}
                  step={900}
                  onChange={(e) => patch(key, { ...d, close_time: e.target.value })}
                  className="bg-white/5 border border-white/10 rounded-md px-2.5 py-1.5 text-sm text-white focus:outline-none focus:border-spark-teal disabled:opacity-60"
                  data-testid={`hours-close-time-${key}`}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const HolidayClosuresSection = ({ closures, onChange }) => {
  const [dateInput, setDateInput] = useState("");
  const [labelInput, setLabelInput] = useState("");

  const add = () => {
    if (!dateInput) return;
    // Prevent duplicates
    if (closures.some((c) => c.date === dateInput)) {
      toast.error("That date is already on the list");
      return;
    }
    const next = [...closures, { date: dateInput, label: labelInput.trim() }].sort((a, b) => a.date.localeCompare(b.date));
    onChange(next);
    setDateInput(""); setLabelInput("");
  };

  const remove = (date) => onChange(closures.filter((c) => c.date !== date));

  // Filter past dates for display only (all still stored)
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = closures.filter((c) => c.date >= today);
  const past = closures.filter((c) => c.date < today);

  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/[0.07] overflow-hidden" data-testid="closures-section">
      <div className="p-5 sm:p-6 border-b border-white/5 flex items-start gap-3">
        <div className="size-9 rounded-md bg-spark-yellow/15 text-spark-yellow flex items-center justify-center shrink-0">
          <CalendarX className="size-4" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-spark-yellow font-bold">Settings</p>
          <h2 className="font-display uppercase tracking-tight text-xl font-bold text-white mt-0.5">Holiday closures</h2>
          <p className="text-xs text-slate-400 mt-1">One-off days when the workshop is closed — public holidays, family time, sick days. Customers can't book these.</p>
        </div>
      </div>
      <div className="p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Date</label>
            <input type="date" value={dateInput} min={today} onChange={(e) => setDateInput(e.target.value)} className={tx + " mt-2"} data-testid="closures-date-input" />
          </div>
          <div className="flex-[2] min-w-[200px]">
            <label className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">Reason (optional)</label>
            <input type="text" value={labelInput} onChange={(e) => setLabelInput(e.target.value)} placeholder="e.g. Boxing Day, family holiday" className={tx + " mt-2"} data-testid="closures-label-input" />
          </div>
          <button type="button" onClick={add} disabled={!dateInput} className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-md bg-spark-yellow text-spark-ink text-xs uppercase tracking-wider font-bold hover:bg-yellow-300 disabled:opacity-40" data-testid="closures-add">
            <Plus className="size-3.5" /> Add
          </button>
        </div>
        {upcoming.length === 0 && past.length === 0 ? (
          <p className="text-xs text-slate-500">No closures on the calendar. Add one above to block a date.</p>
        ) : (
          <ul className="divide-y divide-white/5 rounded-md border border-white/5 overflow-hidden">
            {[...upcoming, ...past].map((c) => (
              <li key={c.date} className={`flex items-center gap-3 px-3 py-2.5 ${c.date < today ? "opacity-60" : ""}`} data-testid={`closure-row-${c.date}`}>
                <span className="font-mono text-sm text-white">{new Date(c.date + "T12:00").toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
                <span className="text-xs text-slate-400 flex-1 truncate">{c.label || "—"}</span>
                {c.date < today && <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">past</span>}
                <button type="button" onClick={() => remove(c.date)} className="p-1.5 text-slate-400 hover:text-red-400" title="Remove" data-testid={`closure-remove-${c.date}`}>
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};


export default AdminSettings;
